/**
 * Backup Section
 *
 * Sidebar widget that lets the user trigger a backup download once they
 * have queued up actions and chosen a backup format.
 *
 * Generates the backup entirely client-side: fetches pending actions from
 * the existing /actions endpoint and pulls full PagerDuty resource snapshots
 * directly from the PD REST API, then triggers a browser file download.
 */

import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Box, Flex, Heading, Text, Button, Badge, Divider } from '@chakra-ui/react';
import { Download } from 'lucide-react';
import { useActions } from '@/providers/actions-provider';
import { useStreamContext } from '@/providers/stream-provider';
import { useBackupContext } from '@/providers/backup-provider';
import { useAppSelector } from '@/stores';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import { STORAGE_KEYS } from '@/config/storage-config';
import { generateBackupJson, triggerBlobDownload } from '@/utils/backup-generator';

export const BackupSection: React.FC = () => {
  const { actions } = useActions();
  const { backupFormat, isBackupDownloaded, setIsBackupDownloaded, hasProceedBeenSubmitted, markProceedSubmitted } = useBackupContext();
  const { currentThreadId, submit, isStreaming } = useStreamContext();
  const selectedAgent = useAppSelector(selectSelectedAgent);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastDownloadedFile, setLastDownloadedFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!backupFormat || actions.length === 0) {
    return null;
  }

  const handleClick = async () => {
    if (isSubmitting || !currentThreadId) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const agentId = selectedAgent?.id || 'optimize-clean-up-agent';
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';
      const { filename, blob } = await generateBackupJson(currentThreadId, agentId, token);
      triggerBlobDownload(filename, blob);
      setLastDownloadedFile(filename);
      setIsBackupDownloaded(true);
      // Advance the chat conversation (if not already done and not mid-stream).
      if (!hasProceedBeenSubmitted && !isStreaming) {
        markProceedSubmitted();
        submit({
          messages: [{ type: 'human' as const, id: uuidv4(), content: 'Backup downloaded. Proceed to execution step.' }],
        }).catch(() => {});
      }
    } catch (e) {
      setError('Download failed — please try again.');
      console.error('[backup] generateBackupJson failed:', e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatLabel = backupFormat.toUpperCase();

  return (
    <>
      <Box>
        <Flex justify="space-between" align="center" mb={3}>
          <Heading size="sm" color="gray.700">
            Backup
          </Heading>
          <Badge colorScheme="blue" borderRadius="full" px={2}>
            {formatLabel}
          </Badge>
        </Flex>

        <Text fontSize="xs" color="gray.500" mb={2}>
          {actions.length} action{actions.length !== 1 ? 's' : ''} ready to back up.
        </Text>

        {(lastDownloadedFile || isBackupDownloaded) && (
          <Text fontSize="xs" color="green.600" mb={2}>
            ✓ {lastDownloadedFile ?? 'Backup downloaded'}
          </Text>
        )}
        {error && (
          <Text fontSize="xs" color="red.500" mb={2}>{error}</Text>
        )}

        <Button
          size="sm"
          colorScheme={isBackupDownloaded ? 'green' : 'blue'}
          leftIcon={<Download size={14} />}
          onClick={handleClick}
          isLoading={isSubmitting}
          loadingText="Preparing..."
          width="100%"
        >
          {isBackupDownloaded ? `Download again (${formatLabel})` : `Download backup (${formatLabel})`}
        </Button>
      </Box>
      <Divider my={3} />
    </>
  );
};
